import { useCallback } from 'react';
import { GridCellParams } from '@mui/x-data-grid-premium';
import { GroupedRowData } from './types';
import { getRowIdAsString } from './groupUtils';

interface UseGroupCheckboxHandlerOptions<T extends GroupedRowData> {
  displayData: T[];
  selectedRows: string[];
  expandGroups: (groupId: string, visibleRowIds: Set<string>) => string[];
  onSelectionChange: (model: {
    ids: Set<string>;
    type: 'include' | 'exclude';
  }) => void;
  getRowId?: (row: T) => string;
}

/**
 * Hook that provides a click handler for group row checkboxes in MUI DataGrid
 *
 * This hook handles the logic for selecting/deselecting all members of a group
 * when the group row's checkbox is clicked. It supports:
 * - Toggling all group members at once
 * - Preserving selections of other groups
 * - Respecting visibility filters
 *
 * @param options Configuration options for the handler
 * @returns A click handler function to be used with onCellClick
 *
 * @example
 * ```tsx
 * const handleCellClick = useGroupCheckboxHandler({
 *   displayData,
 *   selectedRows,
 *   expandGroups,
 *   onSelectionChange: handleRowSelectionChange,
 *   getRowId: (row) => row._id
 * });
 *
 * <DataGrid onCellClick={handleCellClick} ... />
 * ```
 */
export const useGroupCheckboxHandler = <T extends GroupedRowData>({
  displayData,
  selectedRows,
  expandGroups,
  onSelectionChange,
  getRowId = getRowIdAsString,
}: UseGroupCheckboxHandlerOptions<T>) => {
  return useCallback(
    (params: GridCellParams) => {
      // Only handle clicks on group row checkboxes
      if (params.field !== '__check__' || !params.row.isGroup) {
        return;
      }

      // Get all visible row IDs from current displayData
      const visibleRowIds = new Set(displayData.map(row => getRowId(row as T)));

      // Get all member row IDs using the expandGroups function
      const rowId = getRowId(params.row as T);
      const memberIds = expandGroups(rowId, visibleRowIds);

      // Toggle: if all members are selected, deselect them; otherwise select all
      const allSelected = memberIds.every(id => selectedRows.includes(id));

      if (allSelected) {
        // Deselect all members
        const newSelection = selectedRows.filter(id => !memberIds.includes(id));
        const model = { ids: new Set(newSelection), type: 'include' as const };
        onSelectionChange(model);
      } else {
        // Select all members (union with existing selection)
        const newSelection = Array.from(
          new Set([...selectedRows, ...memberIds])
        );
        const model = { ids: new Set(newSelection), type: 'include' as const };
        onSelectionChange(model);
      }
    },
    [displayData, selectedRows, expandGroups, onSelectionChange, getRowId]
  );
};
